'use client'

import { useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { Sparkles, Package } from 'lucide-react'
import { ArticleCard } from './article-card'
import { Article, getArticles, getRayonCategories, BOUTIQUE_EVA_DIAW } from '@/lib/site-config'
import { Button } from '@/components/ui/button'

export function ArticlesGrid({ rayonSlug }: { rayonSlug: string }) {
  const articles = useMemo<Article[]>(() => getArticles(rayonSlug), [rayonSlug])
  const categories = useMemo(() => getRayonCategories(rayonSlug), [rayonSlug])
  const [filter, setFilter] = useState<string | null>(null)

  const filtered = filter ? articles.filter(a => a.category === filter) : articles

  return (
    <div className="mt-6">
      {/* Boutique Eva Diaw ribbon */}
      <div className="mb-4 flex items-center justify-between gap-3 p-3 rounded-lg bg-gradient-to-r from-cuir/5 via-gold/10 to-cuir/5 border border-gold/30">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-full gradient-gold flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-5 w-5 text-cuir" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="font-display text-sm font-semibold text-cuir">
                Sélection {BOUTIQUE_EVA_DIAW.name}
              </span>
              <span className="text-[10px] text-gold uppercase tracking-wider">
                {BOUTIQUE_EVA_DIAW.domain}
              </span>
            </div>
            <p className="text-[11px] text-foreground/70 truncate italic">
              {articles.length} articles en boutique · Curatrice : {BOUTIQUE_EVA_DIAW.founder}
            </p>
          </div>
        </div>
        <Button
          asChild
          size="sm"
          variant="outline"
          className="hidden sm:flex border-gold/40 text-cuir hover:bg-gold/10 hover:text-cuir"
        >
          <a href={BOUTIQUE_EVA_DIAW.url} target="_blank" rel="noopener noreferrer">
            Visiter la boutique
          </a>
        </Button>
      </div>

      {/* Category filter chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter(null)}
          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
            filter === null
              ? 'bg-cuir text-gold border-cuir'
              : 'bg-card text-cuir border-gold/30 hover:border-gold/60'
          }`}
        >
          Tous · {articles.length}
        </button>
        {categories.map(cat => {
          const count = articles.filter(a => a.category === cat).length
          return (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                filter === cat
                  ? 'bg-cuir text-gold border-cuir'
                  : 'bg-card text-cuir border-gold/30 hover:border-gold/60'
              }`}
            >
              {cat} · {count}
            </button>
          )
        })}
      </div>

      {/* Articles grid */}
      <motion.div
        layout
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4"
      >
        {filtered.map(article => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </motion.div>

      {filtered.length === 0 && (
        <div className="py-12 text-center text-foreground/60">
          <Package className="h-10 w-10 mx-auto mb-2 text-gold" />
          Aucun article dans cette catégorie pour le moment.
        </div>
      )}
    </div>
  )
}
